import { useLayoutEffect, useRef, type ReactNode } from 'react'
import { useMobileCaptureOrientation } from '../../hooks/useMobileCaptureOrientation'
import './mobile-capture-shell.css'

type Props = {
  children: ReactNode
}

function resetMobileCaptureScroll(scrollEl: HTMLElement | null) {
  window.scrollTo(0, 0)
  document.documentElement.scrollTop = 0
  document.body.scrollTop = 0
  scrollEl?.scrollTo(0, 0)
}

/**
 * Canonical mobile shell for Image / Video Mobile Capture routes.
 * Owns safe-area padding, scroll container, and portrait guard.
 * Viewport meta is set statically in index.html (viewport-fit=cover).
 */
export default function MobileCaptureShell({ children }: Props) {
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const { showOrientationGuard } = useMobileCaptureOrientation()

  useLayoutEffect(() => {
    document.documentElement.classList.add('mobile-capture-active')
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual'
    }
    resetMobileCaptureScroll(scrollRef.current)
    const raf = window.requestAnimationFrame(() => {
      resetMobileCaptureScroll(scrollRef.current)
    })
    return () => {
      window.cancelAnimationFrame(raf)
      document.documentElement.classList.remove('mobile-capture-active')
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'auto'
      }
    }
  }, [])

  return (
    <div
      className="mc-shell"
      data-testid="mobile-capture-shell"
      data-mobile-capture-shell="v1"
    >
      <div
        ref={scrollRef}
        className="mc-shell__scroll"
        data-testid="mobile-capture-scroll"
        aria-hidden={showOrientationGuard}
      >
        {children}
      </div>
      {showOrientationGuard && (
        <div
          className="mc-orientation-guard"
          role="dialog"
          aria-modal="true"
          aria-labelledby="mc-orientation-guard-title"
          data-testid="mc-orientation-guard"
        >
          <div className="mc-orientation-guard__card">
            <div className="mc-orientation-guard__brand">VISTOERA</div>
            <div className="mc-orientation-guard__icon" aria-hidden="true">📱</div>
            <h2 className="mc-orientation-guard__title" id="mc-orientation-guard-title">
              请竖屏使用
            </h2>
            <p className="mc-orientation-guard__body">
              为了获得更好的上传体验，请将手机旋转为竖屏。
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
